import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import Loader from "../Layout/Loader";
import { AiOutlineCamera } from "react-icons/ai";
import { CloudArrowUpIcon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { loadSeller } from "../../redux/actions/user";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ImageCropperModal from "../Image/ImageCropperModal";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from "../../utils/imageUploadPreparation";

const ShopBanner = ({ isOwner, shop, setShop }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [shopBanner, setShopBanner] = useState(null);
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showBannerMenu, setShowBannerMenu] = useState(false);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const currentProfile = sessionStorage.getItem("currentProfile");
  const dispatch = useDispatch();
  const bannerMenuRef = useRef(null);
  const bannerInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    setShopBanner(shop.banner?.url || null);
  }, [shop]);

  useEffect(() => {
    if (!showBannerMenu) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (bannerMenuRef.current && !bannerMenuRef.current.contains(event.target)) {
        setShowBannerMenu(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showBannerMenu]);

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (file) {
      try {
        const preparedFile = await prepareImageFileForUpload(file);
        setShowBannerMenu(false);
        setSelectedFile(preparedFile);
        setShowBannerCropper(true);
      } catch (error) {
        console.error("Error preparing shop banner:", error);
        setSelectedFile(null);
        setShowBannerCropper(false);
        toast.error(getImagePreparationErrorMessage(error));
      }
    }
  };

  const updateBanner = (croppedFile) => {
    setCroppedFile(croppedFile);
    const previewUrl = URL.createObjectURL(croppedFile);
    setShopBanner(previewUrl);
    setShowSaveButton(true);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) {
      return;
    }

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      setShowBannerMenu(false);
      setSelectedAvatarFile(preparedFile);
      setShowAvatarCropper(true);
    } catch (error) {
      console.error("Error preparing shop avatar:", error);
      setSelectedAvatarFile(null);
      setShowAvatarCropper(false);
      toast.error(getImagePreparationErrorMessage(error));
    }
  };

  const updateAvatar = async (avatarFile) => {
    setIsSavingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("avatar", avatarFile);

      await axios.put(
        `${server}/shop/update-shop-avatar`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
            "Current-Profile": currentProfile,
          },
        }
      );
      dispatch(loadSeller());
      const previewUrl = URL.createObjectURL(avatarFile);
      setShop({ ...shop, avatar: { url: previewUrl } });
      toast.success("Avatar updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update avatar");
      throw error;
    } finally {
      setIsSavingAvatar(false);
    }
  };

  const handleSaveBanner = async () => {
    setIsLoading(true);
    setShowBannerMenu(false);
    try {
      const formData = new FormData();
      formData.append("banner", croppedFile);

      await axios.put(
        `${server}/shop/update-shop-banner`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
            "Current-Profile": currentProfile,
          },
        }
      );
      dispatch(loadSeller());
      toast.success("Banner updated successfully!");
      setShowSaveButton(false);
      setShop({ ...shop, banner: { url: shopBanner } });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update banner");
    } finally {
      setIsLoading(false);
    }
  };

  const openFilePicker = (inputRef) => {
    inputRef.current?.click();
    setShowBannerMenu(false);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="shop-banner-container">
      <img
        src={shopBanner || 'default-banner.png'}
        alt="Shop Banner"
        className="shop-banner-image"
      />
      <div className="banner-overlay"></div>
      {isOwner && (
        <div className="shop-banner-controls">
          <input
            type="file"
            id="shop-banner-background"
            ref={bannerInputRef}
            className="shop-banner-file-input"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleBannerChange}
          />
          <input
            type="file"
            id="shop-profile-avatar-menu-input"
            ref={avatarInputRef}
            className="shop-banner-file-input"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleAvatarChange}
          />
          <div className="shop-banner-action-menu-wrapper" ref={bannerMenuRef}>
            <button
              type="button"
              className="shop-banner-menu-trigger"
              aria-label="Shop hero image options"
              aria-expanded={showBannerMenu}
              onClick={() => setShowBannerMenu((isOpen) => !isOpen)}
            >
              <EllipsisHorizontalIcon className="shop-banner-menu-trigger-icon" aria-hidden="true" />
            </button>
            {showBannerMenu && (
              <div className="shop-banner-action-menu" role="menu">
                <button
                  type="button"
                  className="shop-banner-action-menu-item"
                  role="menuitem"
                  onClick={() => openFilePicker(bannerInputRef)}
                >
                  <AiOutlineCamera className="shop-banner-action-menu-icon" aria-hidden="true" />
                  <span>Edit background image</span>
                </button>
                <button
                  type="button"
                  className="shop-banner-action-menu-item"
                  role="menuitem"
                  disabled={isSavingAvatar}
                  onClick={() => openFilePicker(avatarInputRef)}
                >
                  <AiOutlineCamera className="shop-banner-action-menu-icon" aria-hidden="true" />
                  <span>Edit profile image</span>
                </button>
                {showSaveButton && (
                  <button
                    type="button"
                    className="shop-banner-action-menu-item"
                    onClick={handleSaveBanner}
                    disabled={!croppedFile || isLoading}
                    role="menuitem"
                  >
                    <CloudArrowUpIcon className="shop-banner-action-menu-icon" aria-hidden="true" />
                    <span>Save background image</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showBannerCropper && (
        <ImageCropperModal
          updateAvatar={updateBanner}
          closeModal={() => setShowBannerCropper(false)}
          initialImage={selectedFile}
          aspectRatio={4}
          minWidth={500}
          isAvatar={false}
          isPost={false}
          isLogo={false}
        />
      )}
      {showAvatarCropper && (
        <ImageCropperModal
          updateAvatar={updateAvatar}
          closeModal={() => setShowAvatarCropper(false)}
          initialImage={selectedAvatarFile}
          aspectRatio={1}
          minWidth={200}
          isAvatar={true}
          isPost={false}
          isLogo={false}
        />
      )}
    </div>
  );
};

export default ShopBanner;
